<template>
  <div class="flex flex-col items-center">
    <Segment
      v-if="showTabs"
      v-model="selectedBillingCycle"
      :items="tabs"
      class="mb-[36px] tablet-h:mb-[48px]"
    />
    <Skeleton
      v-else-if="!plans"
      class="mb-[36px] tablet-h:mb-[48px] h-8 w-[237px] rounded-full"
    />
  </div>

  <div class="mx-auto max-w-[1342px]">
    <div
      class="flex flex-col items-stretch gap-[20px] tablet-h:flex-row tablet-h:justify-center tablet-h:gap-[30px]"
    >
      <template v-if="plans">
        <PlanCard
          v-for="plan in filteredPlans"
          :key="plan.id"
          :plan="plan"
          :type="props.type"
          @selected="handleSelectedPlan"
        />
      </template>

      <template v-else>
        <div
          v-for="n in 2"
          :key="n"
          class="w-full rounded-[12px] border border-black/[0.12] bg-white p-[20px] tablet-h:w-[420px] tablet-h:p-[30px]"
        >
          <Skeleton class="h-[34px] w-[208px]" />
          <Skeleton class="mt-[5px] h-[22px] w-[260px]" />
          <Skeleton class="mt-[20px] h-[40px] w-[130px]" />
          <Skeleton class="mt-[9px] h-[34px] w-[160px]" />
          <Skeleton class="mt-[20px] h-[38px] w-full rounded-[7px]" />
          <div class="mt-[26px] flex flex-col gap-[14px]">
            <Skeleton v-for="i in 6" :key="i" class="h-[18px] w-full" />
          </div>
        </div>
      </template>
    </div>

    <p
      class="mt-[48px] text-center text-[14px] leading-[22px] text-gray-600 tablet-h:mt-[64px]"
    >
      Prices shown don't include applicable taxes and fees.
    </p>

    <p class="mt-[24px] text-center text-[18px] leading-[22px] text-gray-800">
      Questions about enterprise security, procurement, or custom contracts?
      <a class="text-link text-[18px]" href="mailto:support@dacr.com">
        Contact Sales
      </a>
    </p>
  </div>
</template>

<script setup lang="ts">
import PlanCard from '@/features/billing/components/PlanCard.vue'
import {
  usePlansQueryQuery,
  type Plan,
  type PlanItem,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { SelectPlanType } from '@/shared/types/index'
import { getTokens } from '@dacr/auth'
import { Segment, Skeleton } from '@dacr/library'
import { computed, ref, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

const props = defineProps<{
  type: SelectPlanType
}>()

const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  { accessToken: getTokens().accessToken },
  { enabled: !webviewStore.isActive }
)

const plans = computed(() => result.value?.plans || billingStore.webviewPlans)

const selectedBillingCycle = ref('')

const subscriptionCycles = ['month', 'year']

const cyclesWithPlans = computed(() =>
  subscriptionCycles.filter(
    (cycle) =>
      plans.value?.groupByBillingCycle.find(
        (billingCycle) => billingCycle.billingCycleType === cycle
      )?.plans.length
  )
)

watchEffect(() => {
  const preferredCycle = [
    plans.value?.activeBillingCycleType,
    plans.value?.defaultBillingCycleType,
  ].find((cycle) => cycle && cyclesWithPlans.value.includes(cycle))

  selectedBillingCycle.value =
    preferredCycle || cyclesWithPlans.value[0] || 'year'
})

const noBillingCyclePlans = computed(
  () =>
    plans.value?.groupByBillingCycle.find(
      (billingCycle) => billingCycle?.billingCycleType === 'none'
    )?.plans || []
)

const showNoBillingCyclePlans = computed(
  () =>
    props.type === SelectPlanType.Register && noBillingCyclePlans.value.length
)

const selectedBillingCyclePlans = computed(
  () =>
    plans.value?.groupByBillingCycle.find(
      (billingCycle) =>
        billingCycle?.billingCycleType === selectedBillingCycle.value
    )?.plans || []
)

const filteredPlans = computed(() =>
  showNoBillingCyclePlans.value
    ? [...noBillingCyclePlans.value, ...selectedBillingCyclePlans.value]
    : selectedBillingCyclePlans.value
)

const tabs = computed(
  () =>
    plans.value?.groupByBillingCycle
      .filter((billingCycle) => billingCycle.billingCycleDescription)
      .map((billingCycle) => ({
        label: billingCycle.billingCycleDescription!,
        value: billingCycle.billingCycleType,
        callout: billingCycle.billingCycleCallout ?? undefined,
      })) || []
)

const showTabs = computed(() => (tabs.value?.length || 0) > 1)

const handleSelectedPlan = async (plan: Plan, planItem: PlanItem) => {
  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: planItem.nativeProductId,
        planId: planItem.nativePlanId,
      },
    })
  }

  if (
    props.type !== SelectPlanType.Register &&
    !billingStore.canUpdateSubscription
  ) {
    return openDialog(Dialog.IAP, { props: { type: 'change' } })
  }

  if (
    props.type === SelectPlanType.Change &&
    planItem.productId === billingStore.billing?.plan.id &&
    plan.items.length === 1
  ) {
    return router.push('/settings/billing')
  }

  const isSubscribedToBundlePlan = plan.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )

  if (
    props.type === SelectPlanType.Change &&
    isSubscribedToBundlePlan &&
    plan.items.length > 1
  ) {
    return openModal(Modals.UpgradeBundleLimit)
  }

  billingStore.setPlanData({
    plan,
    planItem,
    billingCycle: {
      type: selectedBillingCycle.value,
      description:
        plans.value?.groupByBillingCycle.find(
          (billingCycle) =>
            billingCycle?.billingCycleType === selectedBillingCycle.value
        )?.billingCycleDescription || '',
    },
  })

  if (props.type === SelectPlanType.Register) {
    userStore.updateUser({ totalFiles: 0 })
  }

  const isFreePlan = planItem.prices.every((price) => !price.details?.amount)
  if (isFreePlan) {
    return router.push('/home')
  }

  if (props.type === SelectPlanType.Change) {
    return router.push('/change-plans/confirm')
  }

  router.push(`/${props.type.toLowerCase()}/checkout`)
}
</script>
